import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useUserPreferences } from "./store/useStore";
import { useEffect } from "react";
import { trackPageView } from "@/utils/analytics";
import LandingPage from "@/pages/LandingPage";
import StudentForm from "@/pages/StudentForm";
import DashboardLayout from "@/pages/DashboardLayout";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CookiePolicy from "@/pages/CookiePolicy";
import { AdminLogin, AdminDashboard } from "@/pages/admin";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  
  return null;
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const userPreferences = useUserPreferences();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!userPreferences) {
      navigate('/', { replace: true });
    }
  }, [userPreferences, navigate]);
  
  if (!userPreferences) {
    // Show nothing while redirecting to prevent flash
    return null;
  }
  
  return <Component />;
}

// Redirect logged-in users to dashboard
function PublicRoute({ component: Component }: { component: React.ComponentType }) {
  const userPreferences = useUserPreferences();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Only redirect from landing page (not student-form, it handles its own navigation)
    if (userPreferences && location.pathname === '/') {
      console.log('✅ User logged in, redirecting to dashboard from:', location.pathname);
      navigate('/dashboard/home', { replace: true });
    }
  }, [userPreferences, navigate, location.pathname]);
  
  // Show nothing while redirecting to prevent flash (only for landing page)
  if (userPreferences && location.pathname === '/') {
    return null;
  }
  
  return <Component />;
}

function App() {
  const userPreferences = useUserPreferences();
  
  useEffect(() => {
    // Debug: Log user preferences on app load
    console.log('🚀 App loaded, user preferences:', userPreferences);
    
    // Check localStorage backup
    const backup = localStorage.getItem('userPreferences');
    console.log('📦 localStorage backup:', backup);
  }, [userPreferences]);
  
  return (
    <>
      <PWAInstallPrompt />
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<PublicRoute component={LandingPage} />} />
          <Route path="/student-form" element={<PublicRoute component={StudentForm} />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/dashboard/*" element={<ProtectedRoute component={DashboardLayout} />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard/*" element={<AdminDashboard />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
